import type { AccountSnapshot, Activity, PlatformId, PostScore, Topic, TopicStatus } from "../types";
import { latestScore, scoreTotal } from "./research";

/** Column on the content board, and the topic statuses that land in it. 不做 is absent on purpose. */
export const FLOW_COLUMNS = [
  { id: "待确认", statuses: ["待确认"] },
  { id: "制作中", statuses: ["已确认", "制作中"] },
  { id: "待审阅", statuses: ["待审阅"] },
  { id: "待发布", statuses: ["待发布"] },
  { id: "已发布", statuses: ["已完成"] },
] as const;

export type FlowColumnId = (typeof FLOW_COLUMNS)[number]["id"];

const COLUMN_BY_STATUS = new Map<TopicStatus, FlowColumnId>(
  FLOW_COLUMNS.flatMap((column) => column.statuses.map((status) => [status, column.id] as const)),
);

export function flowColumn(status: TopicStatus): FlowColumnId | null {
  return COLUMN_BY_STATUS.get(status) ?? null;
}

export function topicsInColumn(topics: readonly Topic[], column: FlowColumnId): Topic[] {
  const statuses = FLOW_COLUMNS.find((item) => item.id === column)?.statuses ?? [];
  return topics
    .filter((topic) => !topic.inTrash && (statuses as readonly string[]).includes(topic.status))
    .sort((left, right) => stageSort(left, right));
}

function stageSort(left: Topic, right: Topic): number {
  if (left.status === "待确认" || right.status === "待确认") {
    const rank = (topic: Topic) => topic.recommendRank ?? Number.POSITIVE_INFINITY;
    if (rank(left) !== rank(right)) return rank(left) - rank(right);
  }
  return right.updatedAt.localeCompare(left.updatedAt);
}

/** Lower recommendRank comes first. Missing rank stays after every ranked topic. */
export function topByRank(topics: readonly Topic[], limit: number): Topic[] {
  return [...topics]
    .filter((topic) => !topic.inTrash && topic.status === "待确认")
    .sort((left, right) => {
      const leftRank = left.recommendRank ?? Number.POSITIVE_INFINITY;
      const rightRank = right.recommendRank ?? Number.POSITIVE_INFINITY;
      if (leftRank !== rightRank) return leftRank - rightRank;
      return right.updatedAt.localeCompare(left.updatedAt);
    })
    .slice(0, limit);
}

export function stageEnteredAt(topic: Topic, activities: readonly Activity[]): string {
  const stamps = activities
    .filter((item) => item.topicId === topic.id && item.deletedAt == null && item.type === "status_change" && item.toStatus === topic.status)
    .map((item) => item.createdAt)
    .filter((item) => Number.isFinite(Date.parse(item)));
  if (!stamps.length) return topic.updatedAt;
  return stamps.sort((left, right) => right.localeCompare(left))[0] ?? topic.updatedAt;
}

export function hoursSince(stamp: string, now: Date): number {
  const start = Date.parse(stamp);
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, (now.getTime() - start) / 3_600_000);
}

export function stageAgeLabel(stamp: string, now: Date): string {
  const hours = hoursSince(stamp, now);
  if (hours < 1) return "不到 1 小时";
  if (hours < 48) return `${Math.floor(hours)} 小时`;
  return `${Math.floor(hours / 24)} 天`;
}

/** 待确认 sits more than 48 hours. 待审阅 sits more than 24 hours. Other columns are not marked. */
export function columnOverdue(column: FlowColumnId, hours: number): boolean {
  if (column === "待确认") return hours > 48;
  if (column === "待审阅") return hours > 24;
  return false;
}

export function yesterdayOf(today: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

export interface DayDelta {
  likes: number | null;
  saves: number | null;
  followers: number | null;
}

/**
 * Yesterday's new likes, saves, and followers.
 * A number appears only when yesterday and an earlier day both recorded that field.
 * Missing data stays null. Callers must not render null as 0.
 */
export function dayDelta(snapshots: readonly AccountSnapshot[], platform: PlatformId, today: string): DayDelta {
  const empty = { likes: null, saves: null, followers: null };
  const day = yesterdayOf(today);
  if (!day) return empty;
  const rows = snapshots.filter((item) => item.deletedAt == null && item.platform === platform);
  const yesterday = rows.find((item) => item.recordedDate === day);
  const previous = rows
    .filter((item) => item.recordedDate < day)
    .sort((left, right) => right.recordedDate.localeCompare(left.recordedDate))[0];
  if (!yesterday || !previous) return empty;
  return {
    likes: subtract(yesterday.likes, previous.likes),
    saves: subtract(yesterday.saves, previous.saves),
    followers: subtract(yesterday.followers, previous.followers),
  };
}

function subtract(current: number | null, earlier: number | null): number | null {
  if (typeof current !== "number" || typeof earlier !== "number") return null;
  if (!Number.isFinite(current) || !Number.isFinite(earlier)) return null;
  return current - earlier;
}

export function syncedCount(delta: DayDelta): number {
  return [delta.likes, delta.saves, delta.followers].filter((item) => item != null).length;
}

/** Monday through Sunday in the Shanghai calendar day `today`. */
export function weekRange(today: string): { start: string; end: string } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const weekday = date.getUTCDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  const start = new Date(date);
  start.setUTCDate(date.getUTCDate() + mondayOffset);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  return { start: isoDay(start), end: isoDay(end) };
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Posts marked 已完成 whose actual publish day falls in this week.
 * A missing publish time is not counted. Zero means none were recorded this week.
 */
export function publishedThisWeek(topics: readonly Topic[], today: string): number {
  const range = weekRange(today);
  if (!range) return 0;
  return topics.filter((topic) => {
    if (topic.inTrash || topic.status !== "已完成") return false;
    const day = topic.actualPublishAt?.slice(0, 10) ?? "";
    return /^\d{4}-\d{2}-\d{2}$/.test(day) && day >= range.start && day <= range.end;
  }).length;
}

export function matchesColumn(topic: Topic, filter: "全部" | FlowColumnId): boolean {
  const column = flowColumn(topic.status);
  if (!column) return false;
  return filter === "全部" || column === filter;
}

export function scoreLabel(scores: readonly PostScore[], topicId: string): string {
  const score = latestScore(scores, topicId);
  if (!score) return "尚未打分";
  return `${scoreTotal(score)}/40`;
}
